import type {
  NominatimAddress,
  NominatimReverseResult,
  NominatimSearchResult,
} from "./types";

const NOMINATIM = "https://nominatim.openstreetmap.org";
const ACCEPT_LANG = "en";

const searchCache = new Map<string, NominatimSearchResult[]>();
const reverseCache = new Map<string, ReverseHit | null>();

export type ReverseHit = {
  city: string;
  lat: number;
  lon: number;
  displayName: string;
};

export async function searchCity(
  query: string,
  signal?: AbortSignal,
): Promise<NominatimSearchResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const cached = searchCache.get(q);
  if (cached) return cached;

  const url = new URL(`${NOMINATIM}/search`);
  url.searchParams.set("q", q);
  url.searchParams.set("format", "json");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("limit", "5");

  const res = await fetch(url, {
    headers: { "Accept-Language": ACCEPT_LANG },
    signal,
  });
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  const data = (await res.json()) as NominatimSearchResult[];
  searchCache.set(q, data);
  return data;
}

export async function reverseLookup(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<ReverseHit | null> {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
  if (reverseCache.has(key)) return reverseCache.get(key) ?? null;

  const url = new URL(`${NOMINATIM}/reverse`);
  url.searchParams.set("format", "json");
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lon));
  url.searchParams.set("zoom", "10");
  url.searchParams.set("addressdetails", "1");

  const res = await fetch(url, {
    headers: { "Accept-Language": ACCEPT_LANG },
    signal,
  });
  if (!res.ok) throw new Error(`Reverse failed: ${res.status}`);
  const data = (await res.json()) as NominatimReverseResult;

  const city = pickCityName(data.address);
  if (!city) {
    reverseCache.set(key, null);
    return null;
  }
  const hit: ReverseHit = {
    city,
    lat: parseFloat(data.lat),
    lon: parseFloat(data.lon),
    displayName: data.display_name,
  };
  reverseCache.set(key, hit);
  return hit;
}

export function pickCityName(addr: NominatimAddress | undefined): string | null {
  if (!addr) return null;
  return (
    addr.city ??
    addr.town ??
    addr.village ??
    addr.hamlet ??
    addr.municipality ??
    addr.county ??
    null
  );
}

export function describeResult(result: NominatimSearchResult): string {
  const city = pickCityName(result.address);
  const parts: string[] = [];
  if (city) parts.push(city);
  else if (result.name) parts.push(result.name);
  if (result.address?.state && result.address.state !== city) {
    parts.push(result.address.state);
  }
  if (result.address?.country) parts.push(result.address.country);
  return parts.length > 0 ? parts.join(", ") : result.display_name;
}

export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delay: number,
): (...args: TArgs) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: TArgs) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
